import React, { useState, useEffect } from 'react';
import { useChat } from '../../../hooks/useChat';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import toast from 'react-hot-toast';

const ROLE_COLORS = {
  protagonist: 'bg-primary/10 text-primary border-primary/20',
  antagonist: 'bg-destructive/10 text-destructive border-destructive/20',
  supporting: 'bg-success/10 text-success border-success/20',
  minor: 'bg-muted text-muted-foreground border-border',
  chorus: 'bg-warning/10 text-warning border-warning/20',
  narrator: 'bg-secondary/10 text-secondary-foreground border-secondary/20',
};

const ROLE_ICONS = {
  protagonist: 'Crown',
  antagonist: 'Swords',
  supporting: 'Users',
  minor: 'User',
  chorus: 'Music',
  narrator: 'BookOpen',
};

const AICharacterAnalysis = ({ characters, playData, onApplySuggestion }) => {
  const [analysisResult, setAnalysisResult] = useState(null);
  const [hasAnalyzed, setHasAnalyzed] = useState(false);
  const [applyingId, setApplyingId] = useState(null);
  const [expandedChar, setExpandedChar] = useState(null);

  const { response, isLoading, error, sendMessage } = useChat('OPEN_AI', 'gpt-4o', false);

  useEffect(() => {
    if (error) toast?.error(error?.message || 'AI analysis failed');
  }, [error]);

  useEffect(() => {
    if (response && !isLoading && hasAnalyzed) {
      try {
        // Extract JSON from the response
        const jsonMatch = response?.match(/```json\n?([\s\S]*?)\n?```/) ||
          response?.match(/({[\s\S]*})/);
        const jsonStr = jsonMatch ? jsonMatch?.[1] || jsonMatch?.[0] : response;
        const parsed = JSON.parse(jsonStr?.trim());
        setAnalysisResult(parsed);
      } catch (e) {
        // Try to parse the whole response as JSON
        try {
          const parsed = JSON.parse(response?.trim());
          setAnalysisResult(parsed);
        } catch (e2) {
          toast?.error('Could not parse AI analysis. Please try again.');
        }
      }
    }
  }, [response, isLoading, hasAnalyzed]);

  const runAnalysis = () => {
    if (!characters?.length) {
      toast?.error('No characters to analyze.');
      return;
    }

    setAnalysisResult(null);
    setHasAnalyzed(true);
    setExpandedChar(null);

    const characterList = characters
      ?.map(c => `- ${c?.name} (current role: ${c?.roleClassification || 'unset'}, lines: ${c?.lineCount || 0}, scenes: ${c?.sceneAppearances || 0})`)
      ?.join('\n');

    const playTitle = playData?.title || 'Unknown Play';
    const playAuthor = playData?.author || 'Unknown Author';

    const messages = [
      {
        role: 'system',
        content: `You are a Shakespeare scholar and literary analyst specializing in character analysis for educational theater platforms. You provide precise, academically grounded analysis of Shakespeare's characters.`
      },
      {
        role: 'user',
        content: `Analyze the following characters from "${playTitle}" by ${playAuthor}.

Characters:
${characterList}

For each character, provide:
1. **Role Classification**: One of: protagonist, antagonist, supporting, minor, chorus (use "minor" for unnamed/generic roles like "First Citizen", "Servant", etc.)
2. **Confidence**: How confident you are in this classification (high/medium/low)
3. **Reasoning**: Brief 1-2 sentence explanation of the role
4. **Naming Validation**: Whether the character name follows Shakespeare's naming conventions for this play (valid/warning/error) and any notes
5. **Literary Notes**: One key literary insight about this character in the context of the play

Also provide:
- **Play Summary**: A brief 2-sentence summary of the play's main conflict
- **Overall Naming Assessment**: An overall assessment of the character naming conventions across all characters

Respond ONLY with valid JSON in this exact format:
{
  "playSummary": "string",
  "overallNamingAssessment": "string",
  "characters": [
    {
      "name": "CHARACTER_NAME",
      "suggestedRole": "protagonist|antagonist|supporting|minor|chorus",
      "currentRole": "current_role",
      "confidence": "high|medium|low",
      "reasoning": "string",
      "namingValidation": {
        "status": "valid|warning|error",
        "note": "string"
      },
      "literaryNote": "string"
    }
  ]
}`
      }
    ];

    sendMessage(messages, { max_completion_tokens: 3000 });
  };

  const handleApplySuggestion = async (charName, suggestedRole) => {
    if (!onApplySuggestion) return;
    setApplyingId(charName);
    try {
      await onApplySuggestion(charName, suggestedRole);
      toast?.success(`Applied "${suggestedRole}" role to ${charName}`);
    } catch (e) {
      toast?.error(`Failed to apply suggestion for ${charName}`);
    } finally {
      setApplyingId(null);
    }
  };

  const handleApplyAll = async () => {
    if (!analysisResult?.characters?.length || !onApplySuggestion) return;
    const suggestions = analysisResult?.characters?.filter(
      c => c?.suggestedRole && c?.suggestedRole !== c?.currentRole
    );
    if (!suggestions?.length) {
      toast('All roles already match AI suggestions.');
      return;
    }
    for (const char of suggestions) {
      try {
        await onApplySuggestion(char?.name, char?.suggestedRole);
      } catch (e) {
        // continue
      }
    }
    toast?.success(`Applied ${suggestions?.length} role suggestion(s)`);
  };

  const getConfidenceBadge = (confidence) => {
    switch (confidence) {
      case 'high': return 'bg-success/10 text-success';
      case 'medium': return 'bg-warning/10 text-warning';
      case 'low': return 'bg-muted text-muted-foreground';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getNamingStatusIcon = (status) => {
    switch (status) {
      case 'valid': return { icon: 'CheckCircle', color: 'text-success' };
      case 'warning': return { icon: 'AlertTriangle', color: 'text-warning' };
      case 'error': return { icon: 'XCircle', color: 'text-destructive' };
      default: return { icon: 'HelpCircle', color: 'text-muted-foreground' };
    }
  };

  const changedCount = analysisResult?.characters?.filter(
    c => c?.suggestedRole && c?.suggestedRole !== c?.currentRole
  )?.length || 0;

  const namingIssueCount = analysisResult?.characters?.filter(
    c => c?.namingValidation?.status !== 'valid'
  )?.length || 0;

  return (
    <div className="bg-card rounded-lg border border-border p-6 mb-6">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon name="Sparkles" size={20} className="text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-card-foreground">AI Character Analysis</h2>
            <p className="text-sm text-muted-foreground">
              OpenAI-powered role classification and naming validation
            </p>
          </div>
        </div>
        <Button
          onClick={runAnalysis}
          disabled={isLoading || !characters?.length}
          iconName={isLoading ? 'Loader2' : 'Sparkles'}
          size="sm"
        >
          {isLoading ? 'Analyzing...' : hasAnalyzed ? 'Re-Analyze' : 'Analyze Characters'}
        </Button>
      </div>
      {/* Loading State */}
      {isLoading && (
        <div className="flex flex-col items-center justify-center py-10 gap-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          <p className="text-sm text-muted-foreground">
            Analyzing {characters?.length} characters against Shakespeare context...
          </p>
        </div>
      )}
      {/* Idle State */}
      {!isLoading && !analysisResult && !hasAnalyzed && (
        <div className="flex flex-col items-center justify-center py-8 gap-3 text-center">
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
            <Icon name="Brain" size={28} className="text-muted-foreground" />
          </div>
          <div>
            <p className="text-sm font-medium text-card-foreground mb-1">
              AI-Powered Character Intelligence
            </p>
            <p className="text-xs text-muted-foreground max-w-md">
              Click "Analyze Characters" to have OpenAI classify roles (protagonist, antagonist, supporting, minor), 
              validate naming conventions, and provide literary insights for each character.
            </p>
          </div>
        </div>
      )}
      {/* Results */}
      {!isLoading && analysisResult && (
        <div>
          {/* Play Summary */}
          {analysisResult?.playSummary && (
            <div className="bg-muted rounded-lg p-4 mb-5">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="BookOpen" size={16} className="text-primary" />
                <span className="text-sm font-semibold text-card-foreground">Play Context</span>
              </div>
              <p className="text-sm text-muted-foreground">{analysisResult?.playSummary}</p>
            </div>
          )}

          {/* Summary Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
            <div className="bg-muted rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-card-foreground">{analysisResult?.characters?.length || 0}</p>
              <p className="text-xs text-muted-foreground">Characters Analyzed</p>
            </div>
            <div className={`rounded-lg p-3 text-center ${
              changedCount > 0 ? 'bg-primary/10 border border-primary/20' : 'bg-muted'
            }`}>
              <p className={`text-2xl font-bold ${changedCount > 0 ? 'text-primary' : 'text-card-foreground'}`}>
                {changedCount}
              </p>
              <p className="text-xs text-muted-foreground">Role Suggestions</p>
            </div>
            <div className={`rounded-lg p-3 text-center ${
              namingIssueCount > 0 ? 'bg-warning/10 border border-warning/20' : 'bg-muted'
            }`}>
              <p className={`text-2xl font-bold ${namingIssueCount > 0 ? 'text-warning' : 'text-card-foreground'}`}>
                {namingIssueCount}
              </p>
              <p className="text-xs text-muted-foreground">Naming Issues</p>
            </div>
            <div className="bg-muted rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-success">
                {analysisResult?.characters?.filter(c => c?.confidence === 'high')?.length || 0}
              </p>
              <p className="text-xs text-muted-foreground">High Confidence</p>
            </div>
          </div>

          {/* Apply All Button */}
          {changedCount > 0 && onApplySuggestion && (
            <div className="flex items-center justify-between bg-primary/5 border border-primary/20 rounded-lg p-3 mb-5">
              <div className="flex items-center gap-2">
                <Icon name="Wand2" size={16} className="text-primary" />
                <span className="text-sm text-card-foreground">
                  <span className="font-semibold">{changedCount}</span> role{changedCount !== 1 ? 's' : ''} differ from AI suggestions
                </span>
              </div>
              <Button
                size="sm"
                onClick={handleApplyAll}
                iconName="CheckCheck"
              >
                Apply All Suggestions
              </Button>
            </div>
          )}

          {/* Overall Naming Assessment */}
          {analysisResult?.overallNamingAssessment && (
            <div className="bg-muted rounded-lg p-3 mb-5 flex items-start gap-2">
              <Icon name="Tag" size={15} className="text-muted-foreground mt-0.5 shrink-0" />
              <div>
                <span className="text-xs font-semibold text-card-foreground">Naming Assessment: </span>
                <span className="text-xs text-muted-foreground">{analysisResult?.overallNamingAssessment}</span>
              </div>
            </div>
          )}

          {/* Character Cards */}
          <div className="space-y-3">
            {analysisResult?.characters?.map((char, idx) => {
              const isExpanded = expandedChar === char?.name;
              const roleChanged = char?.suggestedRole && char?.suggestedRole !== char?.currentRole;
              const namingStatus = getNamingStatusIcon(char?.namingValidation?.status);
              const roleColor = ROLE_COLORS?.[char?.suggestedRole] || ROLE_COLORS?.minor;
              const roleIcon = ROLE_ICONS?.[char?.suggestedRole] || 'User';

              return (
                <div
                  key={idx}
                  className={`rounded-lg border p-4 transition-all duration-200 ${
                    roleChanged ? 'border-primary/30 bg-primary/5' : 'border-border bg-muted/30'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      {/* Role Icon */}
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 border ${
                        roleColor
                      }`}>
                        <Icon name={roleIcon} size={14} />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className="font-semibold text-card-foreground text-sm">{char?.name}</span>
                          {/* Naming status */}
                          <Icon
                            name={namingStatus?.icon}
                            size={14}
                            className={namingStatus?.color}
                            title={char?.namingValidation?.note}
                          />
                          {/* Confidence badge */}
                          <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                            getConfidenceBadge(char?.confidence)
                          }`}>
                            {char?.confidence}
                          </span>
                        </div>

                        {/* Role comparison */}
                        <div className="flex items-center gap-2 flex-wrap">
                          {roleChanged ? (
                            <>
                              <span className="text-xs text-muted-foreground line-through">
                                {char?.currentRole}
                              </span>
                              <Icon name="ArrowRight" size={12} className="text-primary" />
                              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${
                                roleColor
                              }`}>
                                {char?.suggestedRole}
                              </span>
                            </>
                          ) : (
                            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${
                              roleColor
                            }`}>
                              {char?.suggestedRole}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 shrink-0">
                      {roleChanged && onApplySuggestion && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleApplySuggestion(char?.name, char?.suggestedRole)}
                          disabled={applyingId === char?.name}
                          iconName={applyingId === char?.name ? 'Loader2' : 'Check'}
                          className="text-xs"
                        >
                          Apply
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setExpandedChar(isExpanded ? null : char?.name)}
                        iconName={isExpanded ? 'ChevronUp' : 'ChevronDown'}
                      />
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="mt-4 pt-4 border-t border-border space-y-3">
                      <div>
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Role Reasoning</p>
                        <p className="text-sm text-card-foreground">{char?.reasoning}</p>
                      </div>

                      {char?.namingValidation?.note && (
                        <div>
                          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Naming Convention</p>
                          <div className={`flex items-start gap-2 rounded p-2 ${
                            char?.namingValidation?.status === 'valid' ?'bg-success/5'
                              : char?.namingValidation?.status === 'warning' ?'bg-warning/5' :'bg-destructive/5'
                          }`}>
                            <Icon
                              name={namingStatus?.icon}
                              size={14}
                              className={`${namingStatus?.color} mt-0.5 shrink-0`}
                            />
                            <p className="text-xs text-card-foreground">{char?.namingValidation?.note}</p>
                          </div>
                        </div>
                      )}

                      {char?.literaryNote && (
                        <div>
                          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Literary Insight</p>
                          <p className="text-sm text-muted-foreground italic">"{char?.literaryNote}"</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default AICharacterAnalysis;
